// @ts-strict-ignore
import React from 'react';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import {
    AppComponentProps,
    QuickStartCISetupStep,
    QuickStartCISetupStepType,
    QuickStartCISetupViewProps,
} from '../../../typescriptHelpers';
import { SearchOutlined, CheckCircleFilled } from '@ant-design/icons';
import { InfoTip, setupModuleStyle } from '../../setup/shared';
import { QuestionsInfoSection } from '../QuestionsInfoSection';
import { useScanRepository } from '../hooks/useScanRepository';
import { globalMessage } from '../../../utils/globalMessage';
import { ScanList } from '../ScanList';
import { useDispatch } from 'react-redux';
import { setQuickStartCIStepData } from '../../../actions/ui';
import { selectQuickStartCIStepData } from '../../../selectors/selectors';
import { TrackedColoredButton } from '../ColoredButton';
import * as AnalyticEvents from '../../../utils/analytics';
import { projectNameForPlatform } from '../../../helpers/displayHelpers';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    const [scanResult, isLoading, error, scanRepository] = useScanRepository(orgId, appId);

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error.message);
        }
    }, [error]);

    React.useEffect(() => {
        if (scanResult) {
            dispatch(
                setQuickStartCIStepData({
                    stepType: QuickStartCISetupStepType.ScanDetails,
                    appId,
                    data: {
                        scanResult,
                    },
                }),
            );
        }
    }, [scanResult]);

    return (
        <div
            style={{
                ...setupModuleStyle,
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 0',
                }}
            >
                <Text>Start the scan to continue.</Text>
                <TrackedColoredButton
                    eventName={AnalyticEvents.Event.ClickQuickStartCICta}
                    extraProperties={{
                        ctaType: AnalyticEvents.QuickStartCICTAs.ScanProject,
                    }}
                    icon={scanResult ? <CheckCircleFilled /> : <SearchOutlined />}
                    disabled={isLoading || !!scanResult}
                    loading={isLoading}
                    onClick={() => scanRepository()}
                >
                    {scanResult ? 'Scan complete' : <>{isLoading ? 'Scanning...' : 'Scan project'}</>}
                </TrackedColoredButton>
            </div>
            <ScanList isLoading={isLoading} scanResult={scanResult} appId={appId} orgId={orgId} />
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                We’ll be able to automatically detect most relevant details for your setup via the scan. Next, you’ll be
                able to manually change or enter additional details.
                <br />
                <br />
                Note that all inputs are optional; if a value isn’t specified, we’ll use a placeholder that you can
                manually replace in the corresponding workflow files if desired.
            </InfoTip>
            <QuestionsInfoSection />
        </Space>
    );
};

export const ScanDetails: QuickStartCISetupStep = {
    icon: () => SearchOutlined,
    headerTitle: (platform) => {
        return `Now, we’ll scan your ${projectNameForPlatform(platform)} project in GitHub for relevant details.`;
    },
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: (state, { appId }) => {
        const stepData = selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.ScanDetails);
        return !!stepData?.scanResult;
    },
};
